import { Button, ButtonVariant } from "components/Form/Button";
import { Input } from "components/Form/Input";
import { Field } from "components/Form/Field";
import { Layout } from "components/Layout";
import { Modal } from "components/Modal";
import { memo, useCallback, useState, Dispatch, SetStateAction } from "react";
import { useForm } from "react-hook-form";
import { ReactComponent as Ai } from "assets/ai.svg";
import { SignupScreens } from "types";
import { Storage, StorageKey } from "lib/storage";

type EmailFormValue = {
  email: string;
};

export const EmailConfirmation = memo(function EmailConfirmation(props: {
  email: string;
  setUserEmail: (email: string) => void;
  setStep: Dispatch<SetStateAction<SignupScreens>>;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [showBackWarning, setShowBackWarning] = useState(false);

  const {
    register,
    formState: { isValid, errors },
    watch,
  } = useForm<EmailFormValue>({
    mode: "onChange",
    defaultValues: {
      email: props.email,
    },
  });

  const currentEmail = watch("email");

  const onConfirm = useCallback(() => {
    if (isEditing && currentEmail !== props.email) {
      // Update the email if it was changed
      props.setUserEmail(currentEmail);
    }
    // Proceed to email verification
    props.setStep(SignupScreens.EmailVerification);
  }, [isEditing, currentEmail, props]);

  const handleConfirmBack = useCallback(() => {
    // Clear token and go back
    Storage.remove(StorageKey.TOKEN);
    setShowBackWarning(false);
    props.setStep(SignupScreens.Information);
  }, [props]);

  return (
    <div className="contents">
      <Layout
        className="h-full text-center"
        toolbar={
          <>
            <Button disabled={!isValid} type="button" onClick={onConfirm}>
              Confirm & Continue
            </Button>
            <Button
              variant={ButtonVariant.FLAT}
              onClick={() => setShowBackWarning(true)}
              type="button"
            >
              Back
            </Button>
          </>
        }
      >
        <div className="grid h-full">
          <div className="grid self-start justify-items-center">
            <Ai />

            <h1 className="text-24 leading-1.2 font-semibold mt-4">
              Confirm your email
            </h1>

            <p className="text-858494 mt-3">
              We'll send a verification code to this email address
            </p>
          </div>

          <div className="text-left space-y-6">
            <Field
              label="Email address"
              description={
                errors.email?.message ||
                "Make sure this email is correct before continuing"
              }
              descriptionClassName={
                errors.email?.message ? "text-fd380f" : "text-858494"
              }
              as="label"
            >
              <div className="space-y-3">
                <div className="relative">
                  <Input
                    disabled={!isEditing}
                    type="email"
                    id="email"
                    autoCapitalize="off"
                    autoComplete="email"
                    autoFocus={isEditing}
                    className={!isEditing ? "bg-f4f7ff" : ""}
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value: /^\S+@\S+\.\S+$/,
                        message: "Please enter a valid email address",
                      },
                    })}
                  />
                </div>

                {!isEditing && (
                  <Button
                    variant={ButtonVariant.FLAT_BORDER}
                    onClick={() => setIsEditing(true)}
                    type="button"
                    className="w-full"
                  >
                    Change Email
                  </Button>
                )}

                {isEditing && currentEmail !== props.email && (
                  <div className="p-3 bg-fef3cd border border-f59e0b rounded-lg">
                    <p className="text-14 text-92400e">
                      <strong>Note:</strong> You're changing your email from{" "}
                      <strong>{props.email}</strong> to{" "}
                      <strong>{currentEmail}</strong>
                    </p>
                  </div>
                )}
              </div>
            </Field>

            <div className="space-y-3 p-4 bg-f4f7ff rounded-lg">
              <p className="text-14 font-semibold text-454545">
                What happens next?
              </p>
              <ul className="space-y-2 text-14 text-858494">
                <li className="flex gap-2">
                  <span>1.</span>
                  <span>
                    We'll send a 5-digit verification code to your email
                  </span>
                </li>
                <li className="flex gap-2">
                  <span>2.</span>
                  <span>You'll enter the code to verify your email</span>
                </li>
                <li className="flex gap-2">
                  <span>3.</span>
                  <span>Then you'll create your account password</span>
                </li>
              </ul>
            </div>

            <div className="space-y-3 items-center">
              <p className="text-14 font-semibold text-858494 text-decoration-line: underline">
                IMPORTANT NOTE
              </p>
              <p className="text-14 text-858494">
                Make sure you have access to this email address. The
                verification code will expire in 10 minutes.
              </p>
            </div>
          </div>
        </div>
      </Layout>

      <Modal show={showBackWarning} onClose={() => setShowBackWarning(false)}>
        <div className="space-y-6 text-center">
          <div className="space-y-2">
            <h2 className="text-24 font-semibold">Exit setup?</h2>
            <p className="text-858494">
              You have already registered. If you exit now, you'll need to setup
              a password from the login screen.
            </p>
          </div>

          <div className="flex gap-3">
            <Button
              onClick={() => setShowBackWarning(false)}
              className="flex-1"
            >
              Stay Here
            </Button>
            <Button
              onClick={handleConfirmBack}
              variant={ButtonVariant.FLAT_BORDER}
              className="flex-1"
            >
              Exit
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
});
